import { Phone, Mail, Globe, MapPin, MessageCircle, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { LeadForms } from "@/components/LeadForms";
import { CONTACT, OFFICES } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";

export const ContactSection = () => (
    <section id="contact" data-testid="contact-section" className="blueprint-fine noise relative overflow-hidden py-24 lg:py-36">
        <div className="pointer-events-none absolute -left-40 bottom-0 h-[460px] w-[460px] rounded-full bg-flame/10 blur-[150px]" />
        <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <SectionHeading
                        id="contact-heading"
                        dark
                        overline="Contact OMSIVO"
                        title={<>Tell us what <span className="text-flame">you want to build.</span></>}
                        description="Every enquiry receives a Lead ID and a response from the OMSIVO team. Call, WhatsApp, visit — or start with the form."
                    />

                    <div className="mt-10 space-y-3">
                        <Reveal delay={0.05}>
                            <a href={CONTACT.phoneTel} onClick={() => trackEvent(EVENTS.phoneClick, { location: "contact" })}
                                data-testid="contact-phone-link"
                                className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 transition-colors hover:border-flame/50">
                                <span className="flex items-center gap-4">
                                    <Phone className="h-5 w-5 text-flame" />
                                    <span>
                                        <span className="block font-mono text-[9px] uppercase tracking-[0.24em] text-white/45">Call OMSIVO</span>
                                        <span className="font-display text-lg font-extrabold tracking-[0.1em] text-white">{CONTACT.phoneDisplay}</span>
                                    </span>
                                </span>
                                <ExternalLink className="h-4 w-4 text-white/30 transition-colors group-hover:text-flame" />
                            </a>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                                onClick={() => trackEvent(EVENTS.whatsappClick, { location: "contact" })}
                                data-testid="contact-whatsapp-link"
                                className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 transition-colors hover:border-flame/50">
                                <span className="flex items-center gap-4">
                                    <MessageCircle className="h-5 w-5 text-flame" />
                                    <span>
                                        <span className="block font-mono text-[9px] uppercase tracking-[0.24em] text-white/45">WhatsApp</span>
                                        <span className="font-display text-lg font-extrabold tracking-wide text-white">Chat on WhatsApp</span>
                                    </span>
                                </span>
                                <ExternalLink className="h-4 w-4 text-white/30 transition-colors group-hover:text-flame" />
                            </a>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="grid grid-cols-2 gap-3">
                                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link"
                                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 transition-colors hover:border-flame/50">
                                    <Mail className="h-4 w-4 shrink-0 text-flame" />
                                    <span className="truncate text-sm text-white/80">{CONTACT.email}</span>
                                </a>
                                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4" data-testid="contact-website">
                                    <Globe className="h-4 w-4 shrink-0 text-flame" />
                                    <span className="text-sm text-white/80">{CONTACT.website}</span>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <a href={CONTACT.maps} target="_blank" rel="noopener noreferrer"
                                onClick={() => trackEvent(EVENTS.directionsClick, { location: "contact" })}
                                data-testid="contact-directions-btn"
                                className="flex items-center justify-center gap-3 rounded-full bg-flame py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-all hover:-translate-y-0.5 hover:bg-flame-dark">
                                <MapPin className="h-4 w-4" /> Get Directions
                            </a>
                        </Reveal>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {OFFICES.map((o, i) => (
                            <Reveal key={o.label} delay={0.25 + i * 0.05}>
                                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid={`office-card-${i}`}>
                                    <p className="font-mono text-[9px] uppercase tracking-[0.26em] text-flame">{o.label}</p>
                                    {o.lines.map((l, li) => (
                                        <p key={li} className={li === 0 ? "mt-2 font-display text-sm font-extrabold tracking-wide text-white" : "mt-1 text-xs leading-relaxed text-white/60"}>
                                            {l}
                                        </p>
                                    ))}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal className="lg:col-span-7" delay={0.15}>
                    <div className="rounded-[28px] border border-white/10 bg-omsivo-royal/80 p-6 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-9"
                        data-testid="contact-form-card">
                        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-flame">Start Here</p>
                        <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                            Get Free Estimate
                        </h3>
                        <div className="mt-6">
                            <LeadForms />
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);
